<template>
  <div
    class="range-slider critical-component-clear"
    :class="{
      'range-slider--display': display,
      [$class('disabled')]: disabled,
      [$class('loading')]: loading,
    }"
  >
    <div class="range-slider__ranges">
      <template v-if="single">
        <range-slider-input
          :id="id"
          v-model="localModelValue"
          :config="config"
          :disabled="disabled || loading"
          :label="label"
          :name="name ? name : id"
          :range-width="rangeWidth"
          type="single"
          @interaction-end="handleInteractionEnd"
          @update-value="handleUpdateValue"
        >
          <template #tooltipText="{ isMax, isMin, type, value }">
            <slot
              :is-max="isMax"
              :is-min="isMin"
              name="tooltipText"
              :type="type"
              :value="value"
            />
          </template>
        </range-slider-input>
      </template>

      <template v-else>
        <range-slider-input
          :id="id.min"
          v-model="localModelValue.min"
          :config="{
            ...config,
            max: Math.round(currentMidpoint),
          }"
          :disabled="disabled || loading"
          :label="label.min"
          :name="name ? name : id.min"
          :range-width="rangeWidth.min"
          type="min"
          @interaction-end="handleInteractionEnd"
          @update-value="handleUpdateValue"
        >
          <template #tooltipText="{ isMax, isMin, type, value }">
            <slot
              :is-max="isMax"
              :is-min="isMin"
              name="tooltipText"
              :type="type"
              :value="value"
            />
          </template>
        </range-slider-input>

        <range-slider-input
          :id="id.max"
          v-model="localModelValue.max"
          :config="{
            ...config,
            min: Math.round(currentMidpoint),
          }"
          :disabled="disabled || loading"
          :label="label.max"
          :name="name ? name : id.max"
          :range-width="rangeWidth.max"
          type="max"
          @interaction-end="handleInteractionEnd"
          @update-value="handleUpdateValue"
        >
          <template #tooltipText="{ isMax, isMin, type, value }">
            <slot
              :is-max="isMax"
              :is-min="isMin"
              name="tooltipText"
              :type="type"
              :value="value"
            />
          </template>
        </range-slider-input>
      </template>
    </div>

    <div
      v-if="loading"
      class="range-slider__loading"
    />

    <div
      v-if="localTickMarks.length"
      class="range-slider__tick-marks"
    >
      <div
        v-for="(tickMark) of localTickMarks"
        :key="tickMark.key"
        class="range-slider__tick-mark"
        :style="tickMark.styles"
      />
    </div>

    <div
      v-if="fallbackInputs"
      class="range-slider__fallback-container form-group"
    >
      <template v-if="single">
        <range-slider-fallback
          :id="`${id}-fallback-input`"
          v-model="localModelValue"
          :config="config"
          :disabled="disabled || loading"
          :label="label"
          :name="name ? name : id"
          :input-type="state === 'ready' ? 'number' : 'text'"
          type="single"
          @interaction-end="handleInteractionEnd"
          @update-value="handleUpdateValue"
        />
      </template>

      <template v-else>
        <range-slider-fallback
          :id="`${id.min}-fallback-input`"
          v-model="localModelValue.min"
          :config="config"
          :disabled="disabled || loading"
          :label="label.min"
          :name="name ? name : id.min"
          :other-value="localModelValue.max"
          :input-type="state === 'ready' ? 'number' : 'text'"
          type="min"
          @interaction-end="handleInteractionEnd"
          @update-value="handleUpdateValue"
        />

        <range-slider-fallback
          :id="`${id.max}-fallback-input`"
          v-model="localModelValue.max"
          :config="config"
          :disabled="disabled || loading"
          :label="label.max"
          :name="name ? name : id.max"
          :other-value="localModelValue.min"
          :input-type="state === 'ready' ? 'number' : 'text'"
          type="max"
          @interaction-end="handleInteractionEnd"
          @update-value="handleUpdateValue"
        />
      </template>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Range slider (range-slider)
 * -----------------------------------------------------------------------------
 * Range input slider bar with support for a single value or a range of values.
 * - Continuously updates v-model as user drags range slider.
 * - Using change event wouldn't affect this behaviour for double input range
 *   slider because the min/max values are constantly updated.
 * - Input event is used so range sliders that share values update each other.
 *
 * @param {Object} [config] - Configuration for slider, object is optional but
 * if a property is provided then all properties must be provided.
 * @param {Number} config.max - Range maximum value.
 * @param {Number} config.min - Range minimum value.
 * @param {Number} config.step - Range value steps.
 * @param {Boolean} [disabled] - Input is disabled.
 * @param {Boolean} [display] - Display no handles, input can still be
 * interacted with.
 * @param {Boolean} [fallbackInputs] - Display fallback inputs so user can type
 * in their range values, recommended by Baymard research.
 * @param {Object|String} id - ID for max and min range inputs, pass as string
 * for single range slider.
 * @param {Object|String} label - Labels for max and min range inputs, pass as
 * string for single range slider.
 * @param {Boolean} [loading] - Loading state.
 * @param {Object|Number} modelValue - Current value provided by v-model,
 * if provided a number than a single range slider will be rendered.
 * @param {String} [name] - Input name attribute.
 * @param {Boolean|Array|Number} [tickMarks] - Provide array of values to
 * display tick marks at, provide number to automatically generate that number
 * of tick marks, does not use native functionality due to custom range sliders.
 *
 * @emits interaction-end - When user finishes interacting with inputs.
 * @emits update-value - Debounced emit as the user updates the input value.
 * @emits update:model-value - Emit v-model update event.
 *
 */
import { mapState } from 'vuex'

import RangeSliderFallback from '~async/utils/range-slider/range-slider-fallback'
import RangeSliderInput from '~async/utils/range-slider/range-slider-input'

export default {
  name: 'RangeSlider',

  components: {
    RangeSliderFallback,
    RangeSliderInput,
  },

  props: {
    config: {
      type: Object,
      default: () => ({
        max: 100,
        min: 0,
        step: 1,
      }),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    display: {
      type: Boolean,
      default: false,
    },
    fallbackInputs: {
      type: Boolean,
      default: true,
    },
    id: {
      type: [Object, String],
      default: () => ({
        max: '',
        min: '',
      }),
      required: true,
    },
    label: {
      type: [Object, String],
      default: () => ({
        max: '',
        min: '',
      }),
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: [Object, Number],
      default: () => ({
        max: 100,
        min: 0,
      }),
      required: true,
    },
    name: {
      type: String,
      default: '',
    },
    tickMarks: {
      type: [Boolean, Array, Number],
      default: false,
    },
  },

  emits: {
    'interaction-end': (value) => ['number', 'object'].includes(typeof value),
    'update-value': (value) => ['number', 'object'].includes(typeof value),
    'update:model-value': (value) => ['number', 'object'].includes(typeof value),
  },

  data() {
    return {
      localModelValue: {
        max: 100,
        min: 0,
      },
      state: 'loading',
      timeout: '',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      throttleSetting: (state) => state.index.settings.throttle,
    }),

    /**
     * Compute midpoint between current min and max values.
     * @returns {Number}
     */
    currentMidpoint() {
      if (this.single) {
        return 0
      }

      return (this.localModelValue.max + this.localModelValue.min) / 2
    },

    /**
     * Compute current value difference.
     * @returns {Number}
     */
    currentValueDifference() {
      return this.config.max - this.config.min
    },

    /**
     * Compute local tick marks array.
     * @returns {Array}
     */
    localTickMarks() {
      if (!this.tickMarks) {
        return this.single ? [] : { max: [], min: [] }
      }

      const property = document.dir === 'rtl' ? 'right' : 'left'
      let tickMarksArray = this.tickMarks

      if (typeof this.tickMarks === 'number') {
        const increment = this.config.max / (this.tickMarks - 1)

        tickMarksArray = Array.from({ length: this.tickMarks }).map((_, index) => {
          return index * increment
        })
      }

      /**
       * If there is no difference between the config min and max, just display
       * a tick mark at the start and end.
       */
      if (this.currentValueDifference === 0) {
        return [
          { key: 'tick-mark-min', styles: { [property]: '0%' } },
          { key: 'tick-mark-max', styles: { [property]: '100%' } },
        ]
      }

      return tickMarksArray.map((value, index) => {
        const decimal = (value - this.config.min) / this.currentValueDifference
        const roundedDecimal = decimal.toFixed(2)
        const percent = `${roundedDecimal * 100}%`

        return {
          key: `tick-mark-${value}-${index}`,
          styles: { [property]: percent },
        }
      })
    },

    /**
     * Compute range width.
     * @returns {Object|String}
     */
    rangeWidth() {
      if (this.single) {
        return '100%'
      }

      const maxRangeWidth =
        (this.config.max - this.currentMidpoint) / this.currentValueDifference * 100

      const minRangeWidth =
        (this.currentMidpoint - this.config.min) / this.currentValueDifference * 100

      /**
       * If the max range width or min range width are not a number, fallback to
       * setting both values to 50% width.
       * - Handles cases where range width calculations fail, e.g. when the
       *  `currentValueDifference` is 0.
       */
      if (isNaN(maxRangeWidth) || isNaN(minRangeWidth)) {
        return {
          max: '50%',
          min: '50%',
        }
      }

      return {
        max: `${maxRangeWidth}%`,
        min: `${minRangeWidth}%`,
      }
    },

    /**
     * Compute if single range slider.
     * @returns {Boolean}
     */
    single() {
      return typeof this.modelValue !== 'object'
    },
  },

  watch: {

    /**
     * Watch v-model value to update local value.
     */
    modelValue() {
      this.localModelValue = this.modelValue
    },
  },

  async mounted() {
    this.localModelValue = this.modelValue

    /**
     * Max range slider will sometimes display the wrong value visually.
     * - Force a Vue update to correct initial display error.
     */
    await this.$nextTick()
    this.$forceUpdate()
    this.state = 'ready'
  },

  methods: {

    /**
     * Handle interaction end with input.
     */
    handleInteractionEnd() {
      this.$emit('interaction-end', this.localModelValue)
    },

    /**
     * Handle update value event.
     * @param {Object} payload - Payload data.
     * @param {String} payload.source - 'range' or 'fallback'.
     * @param {Number} payload.value - Updated value.
     * @param {String} payload.type - Type of update.
     */
    handleUpdateValue(payload) {
      const { source, type, value } = payload

      let sanitisedValue = value

      if (!this.single) {
        sanitisedValue = {
          ...this.localModelValue,
          [type]: value,
        }
      }

      this.$emit('update:model-value', sanitisedValue)

      /**
       * Emit debounced event.
       */
      if (this.timeout) {
        clearTimeout(this.timeout)
      }

      const throttle = source === 'fallback' ? 0 : this.throttleSetting

      this.timeout = setTimeout(() => {
        this.$emit('update-value', sanitisedValue)
      }, throttle)
    },
  },
}
</script>

<style lang="scss">
@import './range-slider';
</style>
